// src/pages/user/Deposit.js
import React, { useState, useEffect } from 'react';
import { database, ref, get, set, push, update } from '../../firebase';
import { Wallet, Gift, AlertCircle, Check, CreditCard, Bitcoin, ExternalLink, Smartphone, Tablet, Monitor, History } from 'lucide-react';

const Deposit = () => {
  const [selectedWallet, setSelectedWallet] = useState('fund');
  const [memberId, setMemberId] = useState('');
  const [memberName, setMemberName] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionPassword, setTransactionPassword] = useState('');
  const [userId, setUserId] = useState('');
  const [userFundBalance, setUserFundBalance] = useState(0);
  const [userAddedFund, setUserAddedFund] = useState(0);
  const [userFundCount, setUserFundCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [welcomeBonus, setWelcomeBonus] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [previousDepositTotal, setPreviousDepositTotal] = useState(0);
  const [userDbKey, setUserDbKey] = useState('');

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedUserName = localStorage.getItem('userName');
    const storedDbKey = localStorage.getItem('dbKey') || storedUserId;
    
    if (storedUserId) {
      setUserId(storedUserId);
      setMemberId(storedUserId);
      setMemberName(storedUserName);
      setUserDbKey(storedDbKey);
      fetchUserFundData(storedDbKey);
      fetchPackages();
      fetchPaymentMethods();
      fetchPreviousDeposits(storedUserId);
    }
  }, []);

  // Fetch user fund data from users/{dbKey}/fund
  const fetchUserFundData = async (dbKey) => {
    try {
      const userRef = ref(database, `users/${dbKey}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const fundData = userData.fund || {};
        
        // Get fund data from fund object
        const currentFundBalance = parseFloat(fundData.balance) || 0;
        const currentAddedFund = parseFloat(fundData.total) || 0;
        const currentFundCount = parseInt(fundData.count) || 0;
        
        // Update state
        setUserFundBalance(currentFundBalance);
        setUserAddedFund(currentAddedFund);
        setUserFundCount(currentFundCount);
        
        console.log('Fetched user fund data:', {
          balance: currentFundBalance,
          added: currentAddedFund,
          count: currentFundCount
        });
      }
    } catch (error) {
      console.error('Error fetching user fund data:', error);
    }
  };

  const fetchPreviousDeposits = async (userId) => {
    try {
      const depositsRef = ref(database, 'Deposits');
      const snapshot = await get(depositsRef);
      
      if (snapshot.exists()) {
        const depositsData = snapshot.val();
        let totalDeposit = 0;
        
        // Calculate total deposit amount for this user (only approved deposits)
        Object.keys(depositsData).forEach(key => {
          const deposit = depositsData[key];
          if (deposit.userId === userId && deposit.status === 'approved') {
            totalDeposit += parseFloat(deposit.amount) || 0;
          }
        });
        
        setPreviousDepositTotal(totalDeposit);
      }
    } catch (error) {
      console.error('Error fetching previous deposits:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const packagesRef = ref(database, 'Packages');
      const snapshot = await get(packagesRef);
     
      if (snapshot.exists()) {
        const packagesData = snapshot.val();
        const packagesArray = Object.keys(packagesData).map(key => ({
          id: key,
          ...packagesData[key]
        }));
        setPackages(packagesArray);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const paymentMethodsRef = ref(database, 'PaymentMethods');
      const snapshot = await get(paymentMethodsRef);
      
      if (snapshot.exists()) {
        const methodsData = snapshot.val();
        const methodsArray = Object.keys(methodsData).map(key => ({
          id: key,
          ...methodsData[key]
        }));
        setPaymentMethods(methodsArray);
      } else {
        setPaymentMethods([
          { id: 'bitcoin', name: 'Bitcoin', symbol: 'BTC', icon: '₿' },
          { id: 'usdt_trc20', name: 'USDT (TRC20)', symbol: 'USDT', icon: '💎' },
          { id: 'usdt_bep20', name: 'USDT (BEP20)', symbol: 'USDT', icon: '💎' },
          { id: 'ethereum', name: 'Ethereum', symbol: 'ETH', icon: 'Ξ' },
          { id: 'litecoin', name: 'Litecoin', symbol: 'LTC', icon: 'Ł' },
          { id: 'dogecoin', name: 'Dogecoin', symbol: 'DOGE', icon: 'Ð' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([
        { id: 'bitcoin', name: 'Bitcoin', symbol: 'BTC', icon: '₿' },
        { id: 'usdt_trc20', name: 'USDT (TRC20)', symbol: 'USDT', icon: '💎' },
        { id: 'usdt_bep20', name: 'USDT (BEP20)', symbol: 'USDT', icon: '💎' },
        { id: 'ethereum', name: 'Ethereum', symbol: 'ETH', icon: 'Ξ' },
        { id: 'litecoin', name: 'Litecoin', symbol: 'LTC', icon: 'Ł' },
        { id: 'dogecoin', name: 'Dogecoin', symbol: 'DOGE', icon: 'Ð' }
      ]);
    }
  };

  const calculateWelcomeBonus = (amount, packageId) => {
    if (!packageId) return 0;
    
    const selectedPkg = packages.find(p => p.id === packageId);
    if (!selectedPkg) return 0;
    
    const pkgAmount = parseFloat(amount);
    
    if (pkgAmount >= 10 && pkgAmount <= 249) {
      return (pkgAmount * 0.02).toFixed(2);
    } else if (pkgAmount >= 250 && pkgAmount <= 2499) {
      return (pkgAmount * 0.04).toFixed(2);
    } else if (pkgAmount >= 2500 && pkgAmount <= 9999) {
      return (pkgAmount * 0.06).toFixed(2);
    } else if (pkgAmount >= 10000 && pkgAmount <= 49999) {
      return (pkgAmount * 0.08).toFixed(2);
    } else if (pkgAmount >= 50000 && pkgAmount <= 99999) {
      return (pkgAmount * 0.10).toFixed(2);
    }
    
    return 0;
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    
    if (value) {
      const amountNum = parseFloat(value);
      let matchedPackage = '';
      
      if (amountNum >= 10 && amountNum <= 249) {
        matchedPackage = packages.find(p => p.minInvestment === 10 && p.maxInvestment === 249)?.id || '';
      } else if (amountNum >= 250 && amountNum <= 2499) {
        matchedPackage = packages.find(p => p.minInvestment === 250 && p.maxInvestment === 2499)?.id || '';
      } else if (amountNum >= 2500 && amountNum <= 9999) {
        matchedPackage = packages.find(p => p.minInvestment === 2500 && p.maxInvestment === 9999)?.id || '';
      } else if (amountNum >= 10000 && amountNum <= 49999) {
        matchedPackage = packages.find(p => p.minInvestment === 10000 && p.maxInvestment === 49999)?.id || '';
      } else if (amountNum >= 50000 && amountNum <= 99999) {
        matchedPackage = packages.find(p => p.minInvestment === 50000 && p.maxInvestment === 99999)?.id || '';
      }
      
      setSelectedPackage(matchedPackage);
      
      if (matchedPackage) {
        const bonus = calculateWelcomeBonus(value, matchedPackage);
        setWelcomeBonus(parseFloat(bonus));
      } else {
        setWelcomeBonus(0);
      }
    } else {
      setSelectedPackage('');
      setWelcomeBonus(0);
    }
  };

  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
    const selectedPkg = packages.find(p => p.id === packageId);
    if (selectedPkg) {
      setAmount(selectedPkg.minInvestment.toString());
      const bonus = calculateWelcomeBonus(selectedPkg.minInvestment, packageId);
      setWelcomeBonus(parseFloat(bonus));
    }
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    if (!selectedPaymentMethod) {
      alert('Please select a payment method');
      return;
    }
    
    if (!transactionPassword) {
      alert('Please enter your transaction password');
      return;
    }
    
    setLoading(true);
    
    try {
      const timestamp = new Date().toISOString();
      const depositId = `DEP${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
      const depositAmount = parseFloat(amount);
      const bonusAmount = welcomeBonus;
      const totalToAdd = depositAmount + bonusAmount;
      
      // Calculate new deposit total (previous + current deposit amount WITHOUT bonus)
      const newDepositTotal = previousDepositTotal + depositAmount;
      
      // 1. Update user's fund data in users/{dbKey}/fund
      const userRef = ref(database, `users/${userDbKey}`);
      const userSnapshot = await get(userRef);
      
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        const currentFundData = userData.fund || {};
        
        // Get current fund data
        const currentFundBalance = parseFloat(currentFundData.balance) || 0;
        const currentAddedFund = parseFloat(currentFundData.total) || 0;
        const currentFundCount = parseInt(currentFundData.count) || 0;
        
        // Calculate new values
        const newFundBalance = currentFundBalance + totalToAdd;
        const newAddedFund = currentAddedFund + depositAmount; // Only add deposit amount, not bonus
        const newFundCount = currentFundCount + 1;
        
        // Create updated fund object
        const updatedFundData = {
          balance: newFundBalance,
          total: newAddedFund,
          count: newFundCount,
          lastAmount: totalToAdd,
          lastDate: timestamp,
          lastTransactionId: depositId,
          lastTransactionType: 'Deposit',
          lastTransactionWallet: 'Fund Wallet'
        };
        
        // Update user data in database with fund object
        await update(userRef, {
          fund: updatedFundData
        });

        console.log('Updated user fund data:', {
          oldBalance: currentFundBalance,
          newBalance: newFundBalance,
          oldAdded: currentAddedFund,
          newAdded: newAddedFund,
          oldCount: currentFundCount,
          newCount: newFundCount,
          depositAmount: depositAmount,
          bonusAmount: bonusAmount,
          totalAdded: totalToAdd
        });
        
        // Update local state
        setUserFundBalance(newFundBalance);
        setUserAddedFund(newAddedFund);
        setUserFundCount(newFundCount);
        
        // Update localStorage
        localStorage.setItem('userFundBalance', newFundBalance.toString());
        localStorage.setItem('userAddedFund', newAddedFund.toString());
        localStorage.setItem('userFundCount', newFundCount.toString());
      }
      
      // 2. Create deposit history record in DepositHistory collection
      const depositHistoryRef = push(ref(database, 'DepositHistory'));
      
      const depositHistoryData = {
        depositId: depositId,
        userId: userId,
        userName: memberName,
        amount: depositAmount, // Deposit amount WITHOUT bonus
        bonus: bonusAmount, // Welcome bonus amount
        totalAdded: totalToAdd, // Total added to fund (deposit + bonus)
        paymentMethod: selectedPaymentMethod,
        paymentMethodName: paymentMethods.find(m => m.id === selectedPaymentMethod)?.name || selectedPaymentMethod,
        status: 'approved',
        packageId: selectedPackage,
        packageName: packages.find(p => p.id === selectedPackage)?.plans || '',
        transactionPassword: transactionPassword,
        createdAt: timestamp,
        approvedAt: timestamp,
        
        // Fund update details
        previousFundBalance: userFundBalance,
        newFundBalance: userFundBalance + totalToAdd,
        previousAddedFund: userAddedFund,
        newAddedFund: userAddedFund + depositAmount,
        previousDepositTotal: previousDepositTotal,
        newDepositTotal: newDepositTotal,
        
        // User information
        memberId: userDbKey,
        walletType: 'Fund Wallet',
        type: 'deposit'
      };
      
      await set(depositHistoryRef, depositHistoryData);
      console.log('Deposit history saved with ID:', depositHistoryRef.key);
      
      // 3. Also save to Deposits collection (for backward compatibility)
      const depositRef = push(ref(database, 'Deposits'));
      await set(depositRef, depositHistoryData);
      
      // 4. Create transaction record
      const transactionRef = push(ref(database, 'Transactions'));
      const transactionId = `TXN${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
      
      await set(transactionRef, {
        transactionId: transactionId,
        userId: userId,
        userName: memberName,
        type: 'deposit',
        depositAmount: depositAmount,
        bonusAmount: bonusAmount,
        totalAdded: totalToAdd,
        walletType: 'Fund Wallet',
        depositId: depositId,
        status: 'completed',
        date: timestamp,
        description: `Deposit of $${depositAmount.toFixed(2)} (Bonus: $${bonusAmount.toFixed(2)}) via ${selectedPaymentMethod}`,
        details: {
          previousDepositTotal: previousDepositTotal.toFixed(2),
          newDepositTotal: newDepositTotal.toFixed(2),
          previousFundBalance: userFundBalance,
          newFundBalance: userFundBalance + totalToAdd,
          previousAddedFund: userAddedFund,
          newAddedFund: userAddedFund + depositAmount
        }
      });
      
      // 5. Update local state for previous deposit total
      setPreviousDepositTotal(newDepositTotal);
      
      // 6. Show success message
      const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
      
      alert(`✅ Deposit Completed Successfully!\n\n` +
            `📋 Deposit Details:\n` +
            `Deposit ID: ${depositId}\n` +
            `Deposit Amount: $${depositAmount.toFixed(2)}\n` +
            `Welcome Bonus: $${bonusAmount.toFixed(2)}\n` +
            `Total Added to Fund: $${totalToAdd.toFixed(2)}\n` +
            `Payment Method: ${selectedMethod?.name || selectedPaymentMethod}\n\n` +
            `📊 Fund Wallet Update:\n` +
            `Previous Fund Balance: $${userFundBalance.toFixed(2)}\n` +
            `New Fund Balance: $${(userFundBalance + totalToAdd).toFixed(2)}\n` +
            `Previous Added Fund: $${userAddedFund.toFixed(2)}\n` +
            `New Added Fund: $${(userAddedFund + depositAmount).toFixed(2)}\n` +
            `Fund Count: ${userFundCount} → ${userFundCount + 1}\n\n` +
            `📈 Deposit Total:\n` +
            `Previous Total Deposits: $${previousDepositTotal.toFixed(2)}\n` +
            `New Total Deposits: $${newDepositTotal.toFixed(2)}`);
      
      // Reset form
      setAmount('');
      setTransactionPassword('');
      setSelectedPackage('');
      setWelcomeBonus(0);
      
    } catch (error) {
      console.error('Error submitting deposit:', error);
      alert('❌ Failed to submit deposit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPackageName = () => {
    if (!selectedPackage) return 'No Package Selected';
    const pkg = packages.find(p => p.id === selectedPackage);
    return pkg ? pkg.plans : 'Custom Amount';
  };

  return (
    <div className="max-w-6xl mx-auto pt-4 md:pt-20 px-2 md:px-4">
      <h1 className="text-xl md:text-3xl font-bold text-gold-100 mb-4 md:mb-8">Fund Deposit</h1>
      
      {/* Fund Wallet Stats */}
      <div className="mb-4 md:mb-8 grid grid-cols-3 gap-3 md:gap-4">
        <div className="bg-gradient-to-br from-green-900/40 to-black border border-green-500/50 rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2 md:gap-3">
              <Wallet className="text-2xl md:text-3xl text-green-400" />
              <div>
                <h3 className="text-base md:text-xl font-bold text-green-100">Fund Balance</h3>
                <p className="text-green-400 text-xs md:text-sm">Available Balance</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-green-200">${userFundBalance.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-900/40 to-black border border-blue-500/50 rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2 md:gap-3">
              <History className="text-2xl md:text-3xl text-blue-400" />
              <div>
                <h3 className="text-base md:text-xl font-bold text-blue-100">User Added Fund</h3>
                <p className="text-blue-400 text-xs md:text-sm">Total added by you</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-blue-200">${userAddedFund.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/50 rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2 md:gap-3">
              <Wallet className="text-2xl md:text-3xl text-purple-400" />
              <div>
                <h3 className="text-base md:text-xl font-bold text-purple-100">Fund Count</h3>
                <p className="text-purple-400 text-xs md:text-sm">Total transactions</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-purple-200">{userFundCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit History Summary */}
      <div className="mb-4 md:mb-8 bg-gradient-to-br from-yellow-900/40 to-black border border-yellow-600/50 rounded-xl md:rounded-2xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mb-3 md:mb-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Wallet className="text-2xl md:text-3xl text-yellow-400" />
            <div>
              <h3 className="text-base md:text-xl font-bold text-yellow-100">Total Deposits (Without Bonus)</h3>
              <p className="text-yellow-300 text-xs md:text-sm">Sum of all deposit amounts</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl md:text-3xl font-bold text-yellow-200">${previousDepositTotal.toFixed(2)}</p>
            <p className="text-yellow-400 text-xs md:text-sm">Deposit Amounts Only</p>
          </div>
        </div>
        <div className="mt-3 md:mt-4 p-3 md:p-4 bg-yellow-900/30 rounded-lg">
          <p className="text-yellow-300 text-xs md:text-sm">
            This shows total of all your deposit amounts <span className="font-bold text-yellow-100">without welcome bonus</span>.
            After this deposit, your new deposit total will be: <span className="font-bold text-green-400">
              ${(previousDepositTotal + parseFloat(amount || 0)).toFixed(2)}
            </span>
          </p>
        </div>
      </div>

      {/* Deposit Form */}
      <div className="bg-gradient-to-br from-gold-900/20 to-black border border-gold-600/30 rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8">
        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-8">
          {/* Package Selection */}
          <div>
            <label className="block text-gold-300 text-base md:text-lg font-medium mb-2 md:mb-4">
              Select Package (Optional - Auto-detected from amount)
            </label>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {packages.map(pkg => (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => handlePackageSelect(pkg.id)}
                  className={`px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-medium transition-all text-xs md:text-base ${
                    selectedPackage === pkg.id
                      ? 'bg-gold-500 text-black'
                      : 'bg-gold-600/30 text-gold-100 hover:bg-gold-500/40'
                  }`}
                >
                  {pkg.plans}
                </button>
              ))}
            </div>
            {selectedPackage && (
              <div className="mt-3 md:mt-4 p-3 md:p-4 bg-gold-900/30 rounded-lg">
                <p className="text-gold-300 text-sm md:text-base">
                  Selected: <span className="font-bold text-gold-100">{getPackageName()}</span>
                </p>
              </div>
            )}
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-gold-300 text-base md:text-lg font-medium mb-2 md:mb-3">
              Enter Deposit Amount ($)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              placeholder="Enter amount (e.g., 100)"
              min="10"
              step="0.01"
              required
              className="w-full bg-gold-900/40 border border-gold-600/50 rounded-xl px-4 md:px-6 py-3 md:py-4 text-gold-100 text-lg md:text-2xl focus:outline-none focus:border-gold-400"
            />
            <div className="mt-1 md:mt-2 text-gold-500 text-xs md:text-sm">
              Minimum deposit: $10 | Maximum: $99,999
            </div>
          </div>

          {/* Welcome Bonus Display */}
          {welcomeBonus > 0 && (
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/50 rounded-xl md:rounded-xl p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
                <div className="flex items-center gap-3 md:gap-4">
                  <Gift className="text-2xl md:text-3xl text-green-400" />
                  <div>
                    <h3 className="text-base md:text-xl font-bold text-green-100">Welcome Bonus!</h3>
                    <p className="text-green-300 text-xs md:text-sm">Extra bonus on deposit (added to fund balance)</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl md:text-4xl font-bold text-green-200">+${welcomeBonus.toFixed(2)}</p>
                  <p className="text-green-400 text-xs md:text-sm">
                    {((welcomeBonus / parseFloat(amount || 1)) * 100).toFixed(1)}% Bonus
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <div>
            <label className="block text-gold-300 text-base md:text-lg font-medium mb-2 md:mb-4">
              Select Payment Method
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4">
              {paymentMethods.map(method => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setSelectedPaymentMethod(method.id)}
                  className={`p-3 md:p-4 rounded-lg md:rounded-xl border-2 transition-all flex flex-col items-center justify-center ${
                    selectedPaymentMethod === method.id
                      ? 'border-gold-500 bg-gold-500/20'
                      : 'border-gold-600/30 bg-gold-900/50 hover:border-gold-500/50'
                  }`}
                >
                  <span className="text-2xl md:text-3xl mb-1 md:mb-2">{method.icon}</span>
                  <span className="font-bold text-gold-100 text-xs md:text-base">{method.symbol}</span>
                  <span className="text-xs text-gold-400 mt-1">{method.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Transaction Password */}
          <div>
            <label className="block text-gold-300 text-base md:text-lg font-medium mb-2 md:mb-3">
              Transaction Password
            </label>
            <input
              type="password"
              value={transactionPassword}
              onChange={(e) => setTransactionPassword(e.target.value)}
              placeholder="Enter your transaction password"
              required
              className="w-full bg-gold-900/40 border border-gold-600/50 rounded-xl px-4 md:px-6 py-3 md:py-4 text-gold-100 text-base md:text-xl focus:outline-none focus:border-gold-400"
            />
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-gold-900/40 to-black border border-gold-600/30 rounded-xl md:rounded-xl p-4 md:p-6">
            <h3 className="text-lg md:text-xl font-bold text-gold-100 mb-3 md:mb-4">📋 Deposit Summary</h3>
            <div className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="bg-black/40 p-3 md:p-4 rounded-lg">
                  <p className="text-gold-400 text-xs md:text-sm">Previous Total Deposits</p>
                  <p className="text-blue-300 text-lg md:text-xl font-bold">${previousDepositTotal.toFixed(2)}</p>
                </div>
                <div className="bg-black/40 p-3 md:p-4 rounded-lg">
                  <p className="text-gold-400 text-xs md:text-sm">Current Deposit Amount</p>
                  <p className="text-gold-200 text-lg md:text-xl font-bold">${parseFloat(amount || 0).toFixed(2)}</p>
                </div>
                <div className="bg-green-900/30 p-3 md:p-4 rounded-lg">
                  <p className="text-green-400 text-xs md:text-sm">Welcome Bonus (Extra)</p>
                  <p className="text-green-300 text-lg md:text-xl font-bold">${welcomeBonus.toFixed(2)}</p>
                </div>
                <div className="bg-blue-900/30 p-3 md:p-4 rounded-lg">
                  <p className="text-blue-400 text-xs md:text-sm">New Total Deposits</p>
                  <p className="text-blue-300 text-lg md:text-xl font-bold">
                    ${(previousDepositTotal + parseFloat(amount || 0)).toFixed(2)}
                  </p>
                </div>
              </div>
              
              <div className="bg-black/40 p-4 md:p-6 rounded-lg border border-gold-600/30">
                <p className="text-gold-300 text-sm md:text-base mb-2 md:mb-3">Payment Method:</p>
                <p className="text-xl md:text-2xl font-bold text-gold-100">
                  {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name || 'Not selected'}
                </p>
              </div>
              
              <div className="border-t border-gold-600/30 pt-4 md:pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-base md:text-lg font-bold text-gold-100">Total Added to Fund Wallet:</span>
                  <span className="text-2xl md:text-3xl font-bold text-green-400">
                    ${(parseFloat(amount || 0) + welcomeBonus).toFixed(2)}
                  </span>
                </div>
                <p className="text-gold-500 text-xs md:text-sm mt-1 md:mt-2">
                  This includes: Deposit (${parseFloat(amount || 0).toFixed(2)}) + Bonus (${welcomeBonus.toFixed(2)})
                </p>
                <div className="mt-3 md:mt-4 grid grid-cols-2 gap-2 md:gap-4">
                  <div className="bg-green-900/20 p-2 md:p-3 rounded-lg">
                    <p className="text-green-400 text-xs md:text-sm">New Fund Balance</p>
                    <p className="text-green-300 text-sm md:text-base font-bold">
                      ${(userFundBalance + parseFloat(amount || 0) + welcomeBonus).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-blue-900/20 p-2 md:p-3 rounded-lg">
                    <p className="text-blue-400 text-xs md:text-sm">New Added Fund</p>
                    <p className="text-blue-300 text-sm md:text-base font-bold">
                      ${(userAddedFund + parseFloat(amount || 0)).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center pt-2 md:pt-4">
            <button
              type="submit"
              disabled={loading || !amount || !selectedPaymentMethod || !transactionPassword}
              className="relative w-full md:w-auto bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 disabled:from-gold-700 disabled:to-gold-800 disabled:cursor-not-allowed text-black font-bold py-4 md:py-5 px-6 md:px-24 rounded-full md:rounded-full transition-all text-base md:text-xl transform hover:scale-105 active:scale-95 shadow-lg shadow-gold-500/30 disabled:shadow-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2 md:gap-3">
                  <div className="animate-spin rounded-full h-5 md:h-6 w-5 md:w-6 border-b-2 border-black"></div>
                  <span className="text-sm md:text-base">Processing...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2 md:gap-3">
                  <CreditCard size={20} />
                  <span className="text-sm md:text-base">SUBMIT DEPOSIT</span>
                  <Bitcoin size={20} />
                </span>
              )}
            </button>
            
            <p className="text-gold-400 text-xs md:text-sm mt-2 md:mt-4">
              ✅ Deposit will update your Fund Wallet immediately in users/{userDbKey}/fund
            </p>
          </div>
        </form>

        {/* Information Box */}
        <div className="mt-4 md:mt-8 p-4 md:p-6 bg-blue-900/20 border border-blue-500/30 rounded-xl md:rounded-xl">
          <div className="flex items-start gap-2 md:gap-3">
            <AlertCircle className="text-blue-400 mt-1 flex-shrink-0" size={20} />
            <div>
              <h4 className="text-base md:text-lg font-bold text-blue-100 mb-1 md:mb-2">Database Update Information</h4>
              <ul className="text-blue-200 text-xs md:text-sm space-y-1 md:space-y-2">
                <li>• <strong>Fund Balance</strong> in <code>users/{userDbKey}/fund/balance</code> will increase by: Deposit + Bonus</li>
                <li>• <strong>User Added Fund</strong> in <code>users/{userDbKey}/fund/total</code> will increase by: Deposit amount only</li>
                <li>• <strong>Fund Count</strong> in <code>users/{userDbKey}/fund/count</code> will increase by 1</li>
                <li>• <strong>Deposit Records</strong> will be saved in <code>Deposits</code> collection</li>
                <li>• <strong>User Fund Transactions</strong> will be saved in <code>UserFundTransactions/{userDbKey}</code></li>
                <li>• All updates happen in real-time and are automatically approved</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* User Information */}
      <div className="mt-4 md:mt-8 bg-gradient-to-br from-gold-900/20 to-black border border-gold-600/30 rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8">
        <h2 className="text-lg md:text-2xl font-bold text-gold-100 mb-3 md:mb-6">👤 Deposit Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          <div className="bg-gold-900/30 border border-gold-600/30 rounded-xl p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
              <span className="text-xl md:text-2xl">👤</span>
              <div>
                <div className="text-gold-400 text-xs md:text-sm">Member Name</div>
                <div className="text-base md:text-lg font-bold text-gold-100">{memberName}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gold-900/30 border border-gold-600/30 rounded-xl p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
              <span className="text-xl md:text-2xl">🆔</span>
              <div>
                <div className="text-gold-400 text-xs md:text-sm">Member ID</div>
                <div className="text-base md:text-lg font-bold text-gold-100">{memberId}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gold-900/30 border border-gold-600/30 rounded-xl p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
              <span className="text-xl md:text-2xl">💰</span>
              <div>
                <div className="text-gold-400 text-xs md:text-sm">Total Deposits</div>
                <div className="text-base md:text-lg font-bold text-blue-100">${previousDepositTotal.toFixed(2)}</div>
                <div className="text-xs text-gold-500">(Without bonus)</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gold-900/30 border border-gold-600/30 rounded-xl p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
              <span className="text-xl md:text-2xl">📊</span>
              <div>
                <div className="text-gold-400 text-xs md:text-sm">Database Key</div>
                <div className="text-base md:text-lg font-bold text-gold-100 truncate">{userDbKey}</div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="mt-4 md:mt-6 p-3 md:p-4 bg-black/40 rounded-lg">
          <p className="text-gold-400 text-xs md:text-sm">
            Database Path: <code className="text-gold-200">users/{userDbKey}/fund</code>
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default Deposit;
